<template>
    <div class="grid ls-billing-information__container">
        <div class="grid__col-12">
            <h2 class="section-title">Billing Information</h2>
            <div class="ls-billing-information--three-days">
                <span class="ls-highlight"
                    >You’ll have a chance to review your service before your card is charged.</span
                >
            </div>
        </div>

        <ls-credit-card ref="creditCard"></ls-credit-card>

        <div class="grid">
            <div class="grid__col-sm-5" style="min-width: 275px;">
                <ls-promo v-if="!isStandAloneTreatment" test-id="promocode-field" />
            </div>
            <div class="grid__col-sm-6">
                <!-- to prevent inputs being on same line not 7 to avoid min-width issues -->
            </div>
            <div class="grid__col-sm-5" style="min-width: 275px;">
                <label class="ls-label ls-margin--top" for="leadSource">
                    How did you hear about us?
                </label>
                <div class="ls-input__wrapper for-select">
                    <select
                        id="leadSource"
                        v-model="leadSource"
                        v-ls-track.change="{
                            target: 'order-form--lead-source',
                            attachValue: true,
                        }"
                        class="ls-input"
                        @change="updateLeadSourceState()"
                    >
                        <option
                            v-for="leadSource in leadSourceOptions"
                            :key="leadSource.value"
                            :value="leadSource.value"
                        >
                            {{ leadSource.name }}
                        </option>
                    </select>
                </div>
                <span class="ls-billing-information-form--thank"
                    >We'd <i class="fa fa-heart"></i> to know who to thank! (optional)</span
                >
            </div>
            <div class="grid__col-sm-6">
                <!-- to prevent inputs being on same line not 7 to avoid min-width issues -->
            </div>
        </div>

        <div v-if="mobile" class="grid">
            <div class="grid__col-12">
                <div class="ls-billing-information-form--tos-container">
                    <label
                        v-ls-track.click="{
                            target: 'order-form--policy-checkbox',
                        }"
                        for="terms-of-service"
                        @click.prevent="updateBillingInformationValidationState()"
                    >
                        <input
                            id="terms-of-service"
                            v-model="agreeToPolicies"
                            type="checkbox"
                            name="terms-of-service"
                        />
                        <branded-checkbox :is-active="agreeToPolicies" :is-big-icon="true" />
                        <span
                            class="ls-billing-information-form--tos"
                            :class="{ 'ls-billing-information-form--tos-invalid': tosInvalid }"
                        >
                            I agree to the terms of service and
                            {{ isStandAloneTreatment ? '48-hour reschedule' : 'long grass' }} policy
                            <strong>&nbsp;(required)</strong>
                        </span>
                    </label>
                </div>
                <div class="ls-margin--top">
                    <a
                        v-ls-track.click="{
                            target: 'terms-of-service--link',
                        }"
                        class="ls-color--blue ls-billing-information-form--tos"
                        @click="openModal('termsOfService')"
                        >Terms of service <i class="fa fa-external-link"></i
                    ></a>
                    <br />
                    <a
                        v-if="isStandAloneTreatment"
                        v-ls-track.click="{
                            target: '48-hour--link',
                        }"
                        class="ls-color--blue ls-billing-information-form--tos"
                        test-id="48-hour-mobile"
                        @click="openModal('reschedule48')"
                    >
                        48-hour reschedule <i class="fa fa-external-link"></i>
                    </a>
                    <a
                        v-else
                        v-ls-track.click="{
                            target: 'long-grass-policy--link',
                        }"
                        class="ls-color--blue ls-billing-information-form--tos"
                        @click="openModal('longGrass')"
                        >Long grass policy <i class="fa fa-external-link"></i
                    ></a>
                </div>
            </div>
        </div>
        <div v-else class="grid__col-12">
            <div class="ls-billing-information-form--tos-container">
                <label
                    v-ls-track.click="{
                        target: 'order-form--policy-checkbox',
                    }"
                    for="terms-of-service"
                    @click.prevent="updateBillingInformationValidationState()"
                >
                    <input
                        id="terms-of-service"
                        v-model="agreeToPolicies"
                        type="checkbox"
                        name="terms-of-service"
                    />
                    <branded-checkbox :is-active="agreeToPolicies" :is-big-icon="true" />
                    <span
                        class="ls-billing-information-form--tos"
                        :class="{ 'ls-billing-information-form--tos-invalid': tosInvalid }"
                    >
                        I agree to the
                        <a
                            v-ls-track.click="{
                                target: 'terms-of-service--link',
                            }"
                            class="ls-color--blue"
                            @click.prevent.stop="openModal('termsOfService')"
                            >terms of service</a
                        >
                        and
                        <a
                            v-if="isStandAloneTreatment"
                            v-ls-track.click="{
                                target: '48-hour--link',
                            }"
                            class="ls-color--blue"
                            test-id="48-hour"
                            @click.prevent.stop="openModal('reschedule48')"
                        >
                            48-hour reschedule
                        </a>
                        <a
                            v-else
                            v-ls-track.click="{
                                target: 'long-grass-policy--link',
                            }"
                            class="ls-color--blue"
                            @click.prevent.stop="openModal('longGrass')"
                            >long grass policy</a
                        >
                        <strong>&nbsp;(required)</strong>
                    </span>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import _ from 'lodash';
import * as types from '@/store/types';
import brandedCheckbox from '@/components/original-mydot/branded-checkbox/BrandedCheckbox.vue';
import eventBusService from '@/services/event-bus.service';
import lsCreditCard from '@/components/original-mydot/credit-card/CreditCard.vue';
import lsPromo from '@/components/original-mydot/promo/Promo.vue';
import { serviceType, leadSourceOptions } from '@/constants';

export default {
    components: {
        brandedCheckbox,
        lsCreditCard,
        lsPromo,
    },
    data() {
        return {
            agreeToPolicies: false,
            tosInvalid: false,
            leadSource: null,
            leadSourceOptions,
        };
    },
    computed: {
        ...mapState(['order', 'billingInformation', 'screenSize']),
        ...mapGetters({
            mobile: types.SCREEN_SIZE__GET_MOBILE_SCREEN,
        }),
        isStandAloneTreatment() {
            return serviceType.TREATMENT === this.order.intent;
        },
    },
    created() {
        const { value } = _.find(leadSourceOptions, ['value', _.get(this, 'order.lead_source')]);
        if (value) {
            this.leadSource = value;
        }
        this.agreeToPolicies = _.get(this, 'billingInformation.valid') || this.agreeToPolicies;
    },
    mounted() {
        eventBusService.eventBus.$on('submitButtonClick', () => {
            if (!this.agreeToPolicies) {
                this.tosInvalid = true;
            }
        });
    },
    methods: {
        ...mapActions({
            openModal: types.MODAL__OPEN_MODAL,
            updateBillingInformationValidation:
                types.BILLING_INFORMATION__UPDATE_PAYMENT_DETAILS_VALIDATION,
            updateLeadSource: types.BILLING_INFORMATION__UPDATE_LEAD_SOURCE,
            orderUpdateOrder: types.ORDER__UPDATE_ORDER,
        }),
        updateBillingInformationValidationState() {
            this.agreeToPolicies = !this.agreeToPolicies;
            this.tosInvalid = !this.agreeToPolicies;
            this.updateBillingInformationValidation(this.agreeToPolicies);
        },
        updateLeadSourceState() {
            this.updateLeadSource({ lead_source: this.leadSource });
        },
    },
};
</script>

<style lang="scss">
.ls-billing-information-form--tos-container {
    margin-top: rem(1);

    label {
        display: flex;
        align-items: center;
        user-select: none;
        cursor: pointer;

        input {
            display: none;
        }

        i {
            margin-right: rem(-5);
        }
    }
}

.ls-billing-information-form--tos {
    font-size: rem(-2);
}

.ls-billing-information-form--thank {
    padding-top: rem(-6);
    font-size: rem(-2);
    color: palette(gray, light);
}

.ls-billing-information-form--tos-invalid {
    color: palette(red);
    > a {
        text-decoration: underline;
        color: palette(red) !important;
    }
}

.ls-billing-information--three-days {
    margin-top: rem(1);
    font-size: rem(-2);
}
.themed-app {
    .ls-billing-information-form--thank {
        color: $theme-color-gray-dark;
    }

    .ls-billing-information-form--tos-invalid {
        color: $theme-color-danger;
        > a {
            color: $theme-color-danger !important;
        }
    }
}
</style>
